import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { STATE_STATS, MAX_STATE_COUNT } from "../lib/network";

const HQ_STATE = "Maharashtra"; // Mumbai head office — origin of supply beams

// Map India lng/lat to the SVG canvas.
const W = 460, H = 520;
const LNG0 = 68, LNG1 = 98, LAT0 = 6, LAT1 = 38;
const project = ([lng, lat]) => [
  ((lng - LNG0) / (LNG1 - LNG0)) * W,
  ((LAT1 - lat) / (LAT1 - LAT0)) * H,
];

const nodes = STATE_STATS.map((s) => {
  const [x, y] = project(s.coords);
  const r = 5 + (s.count / MAX_STATE_COUNT) * 12;
  return { ...s, x, y, r };
});

const hqNode = nodes.find((n) => n.state === HQ_STATE) || nodes[0];
const beamTargets = nodes.filter((n) => n.state !== HQ_STATE);

// Thin glowing supply lines that periodically shoot from the Mumbai HQ node
// out to partner state nodes — round-robin so every state is reached over time.
const SupplyBeams = ({ hq, targets }) => {
  const [beams, setBeams] = useState([]);
  const idxRef = useRef(0);
  const uidRef = useRef(0);

  useEffect(() => {
    if (!hq || targets.length === 0) return;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (mq.matches) return;
    const timer = setInterval(() => {
      const t = targets[idxRef.current % targets.length];
      idxRef.current += 1;
      const id = uidRef.current++;
      setBeams((b) => [...b, { id, t }]);
    }, 950);
    return () => clearInterval(timer);
  }, [hq, targets]);

  const remove = (id) => setBeams((b) => b.filter((x) => x.id !== id));
  if (!hq) return null;

  return (
    <g data-testid="supply-beams" style={{ pointerEvents: "none" }}>
      {beams.map(({ id, t }) => (
        <g key={id}>
          <motion.line
            x1={hq.x} y1={hq.y} x2={t.x} y2={t.y}
            stroke="#E8A85C" strokeWidth="1.3" strokeLinecap="round"
            style={{ filter: "drop-shadow(0 0 3px rgba(184,115,51,0.9))" }}
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: [0, 0.9, 0.9, 0] }}
            transition={{ duration: 1.9, ease: "easeInOut", times: [0, 0.2, 0.75, 1] }}
            onAnimationComplete={() => remove(id)}
          />
          <motion.circle
            cx={t.x} cy={t.y} r="3"
            fill="none" stroke="#E8A85C" strokeWidth="1"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: [0, 3.6], opacity: [0.9, 0] }}
            transition={{ duration: 0.9, delay: 1.35, ease: "easeOut" }}
            style={{ transformOrigin: `${t.x}px ${t.y}px` }}
          />
        </g>
      ))}
    </g>
  );
};

export const NetworkMap = ({ activeState, onSelect }) => {
  const [hover, setHover] = useState(null);
  const active = hover || nodes.find((n) => n.state === activeState) || null;

  return (
    <div className="grid lg:grid-cols-[1.15fr_0.85fr] gap-10 items-center" data-testid="network-map">
      {/* Map canvas */}
      <div className="relative">
        <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-auto max-h-[520px] overflow-visible">
          <defs>
            <radialGradient id="nodeGlow" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#B87333" stopOpacity="0.55" />
              <stop offset="100%" stopColor="#B87333" stopOpacity="0" />
            </radialGradient>
          </defs>

          {/* faint lat/long grid for a cartographic feel */}
          {[0.25, 0.5, 0.75].map((f) => (
            <g key={f} stroke="#ffffff" strokeOpacity="0.05">
              <line x1={f * W} y1="0" x2={f * W} y2={H} />
              <line x1="0" y1={f * H} x2={W} y2={f * H} />
            </g>
          ))}

          {/* connective lines to nearest neighbours for a network mesh */}
          {nodes.map((a, i) =>
            nodes.slice(i + 1).map((b) => {
              const d = Math.hypot(a.x - b.x, a.y - b.y);
              if (d > 95) return null;
              return (
                <line
                  key={`${a.state}-${b.state}`}
                  x1={a.x} y1={a.y} x2={b.x} y2={b.y}
                  stroke="#B87333" strokeOpacity="0.12" strokeWidth="0.6"
                />
              );
            })
          )}

          {/* animated glowing supply beams from HQ (Mumbai) to state nodes */}
          <SupplyBeams hq={hqNode} targets={beamTargets} />

          {/* HQ pulse ring + label */}
          {hqNode && (
            <g data-testid="map-hq-node" style={{ pointerEvents: "none" }}>
              <motion.circle
                cx={hqNode.x} cy={hqNode.y} r={hqNode.r + 6}
                fill="none" stroke="#E8A85C" strokeWidth="1"
                animate={{ scale: [1, 1.7], opacity: [0.6, 0] }}
                transition={{ duration: 2.2, repeat: Infinity, ease: "easeOut" }}
                style={{ transformOrigin: `${hqNode.x}px ${hqNode.y}px` }}
              />
              <text
                x={hqNode.x + hqNode.r + 7} y={hqNode.y + 3}
                fill="#E8A85C" fontSize="9" fontFamily="serif" letterSpacing="0.5"
              >
                HQ · Mumbai
              </text>
            </g>
          )}

          {/* state nodes */}
          {nodes.map((n, i) => {
            const isActive = active?.state === n.state;
            const isSel = activeState === n.state;
            return (
              <g
                key={n.state}
                data-testid={`map-node-${n.state.replace(/\s+/g, "-").toLowerCase()}`}
                className="cursor-pointer"
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(null)}
                onClick={() => onSelect?.(isSel ? "All" : n.state)}
              >
                <motion.circle
                  cx={n.x} cy={n.y} r={n.r + 9} fill="url(#nodeGlow)"
                  initial={{ opacity: 0 }} whileInView={{ opacity: isActive || isSel ? 1 : 0.5 }}
                  viewport={{ once: true }} transition={{ delay: (i % 12) * 0.03 }}
                />
                <motion.circle
                  cx={n.x} cy={n.y} r={n.r}
                  fill={isSel ? "#B87333" : "#0e0e0e"}
                  stroke="#B87333" strokeWidth={isActive || isSel ? 2 : 1}
                  initial={{ scale: 0, opacity: 0 }}
                  whileInView={{ scale: 1, opacity: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: (i % 12) * 0.03, type: "spring", stiffness: 200, damping: 16 }}
                  style={{ transformOrigin: `${n.x}px ${n.y}px` }}
                />
                <circle cx={n.x} cy={n.y} r={Math.max(1.5, n.r * 0.28)} fill="#B87333" />
              </g>
            );
          })}
        </svg>

        {/* hover tooltip */}
        {active && (
          <div
            className="pointer-events-none absolute left-1/2 -translate-x-1/2 top-3 bg-[#0e0e0e]/95 border border-[#B87333]/40 rounded-sm px-4 py-2 backdrop-blur"
            data-testid="map-tooltip"
          >
            <div className="font-serif text-base text-[#F5F5F0]">{active.state}</div>
            <div className="font-body text-xs text-[#8A8A82] mt-0.5">
              {active.count} partners · {active.verified} verified · {active.topCity}
            </div>
          </div>
        )}
      </div>

      {/* Side legend / summary */}
      <div>
        <span className="overline text-[#B87333]">/ Coverage</span>
        <h3 className="font-serif text-3xl md:text-4xl mt-4 leading-tight">
          Present in {STATE_STATS.length} states &amp; UTs.
        </h3>
        <p className="font-body text-[#8A8A82] mt-4 leading-relaxed max-w-md">
          Each node is a state we serve — sized by the depth of our partner channel there.
          Tap a node to explore its partners below.
        </p>
        <div className="flex items-center gap-6 mt-8">
          <div className="flex items-center gap-2 font-body text-xs text-[#8A8A82]">
            <span className="w-2.5 h-2.5 rounded-full bg-[#0e0e0e] border border-[#B87333]" /> Fewer partners
          </div>
          <div className="flex items-center gap-2 font-body text-xs text-[#8A8A82]">
            <span className="w-4 h-4 rounded-full bg-[#0e0e0e] border-2 border-[#B87333]" /> Deep channel
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-8">
          {[...STATE_STATS].sort((a, b) => b.count - a.count).slice(0, 6).map((s) => (
            <button
              key={s.state}
              data-testid={`map-legend-${s.state.replace(/\s+/g, "-").toLowerCase()}`}
              onMouseEnter={() => setHover(s)}
              onMouseLeave={() => setHover(null)}
              onClick={() => onSelect?.(activeState === s.state ? "All" : s.state)}
              className={`text-left rounded-sm border px-3 py-2 transition-colors ${activeState === s.state ? "border-[#B87333] bg-[#B87333]/10" : "border-white/10 hover:border-[#B87333]/50"}`}
            >
              <div className="font-body text-[11px] text-[#C9C9C2] truncate">{s.state}</div>
              <div className="font-serif text-lg text-[#F5F5F0]">{s.count}</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
